import { Grid2, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	Carrier,
	CARRIER_INITIAL_VALUES,
} from "@/pages/catalogs/models/Carrier";
import { Stand } from "@/pages/configuration/models/Stand";
import {
	StandGrid,
	useStandsByPart,
} from "@/pages/configuration/pages/parts/StandGrid";
import { setStandsToPart } from "@/pages/configuration/repositories/StandRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LocationTreeSelector } from "../../../../../shared/selectors";
import { upsertCarrier } from "../../../repositories/CarrierRepository";
import { assemblyStatusCodes } from "@/pages/mill/carriers/components/CarrierAssemblyStatusTag";

type Props = {
	carrier: Carrier | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const CarrierDetails = ({ carrier, modal, onHide }: Props) => {
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);

	const succesfullyMessage = `The carrier was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<Carrier>({
		defaultValues: CARRIER_INITIAL_VALUES,
		mode: "onChange",
	});

	const { data: standsOfPart, isSuccess: standsIsSuccess } = useStandsByPart({
		partId: carrier?.PartId!,
		onError: (error) => setError(error.message),
	});
	const standsJson = JSON.stringify(
		standsSelected.map((stand: Stand) => ({
			...stand,
			PartId: carrier?.PartId,
			PartName: carrier?.PartName,
		})),
	);
	const oldStandsJson = JSON.stringify(standsOfPart);
	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) ||
		standsJson !== oldStandsJson ||
		modal == "create";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertCarrier, {
		onSuccess: async (id: number) => {
			if (id) {
				const resp = await setStandsToPart(id, standsSelected);
				if (!resp.ok) setError(resp.message);
			}

			setNotificationMessage(succesfullyMessage);
			await queryClient.invalidateQueries(["carriers"]);
			await queryClient.invalidateQueries(["standsByPart", id]);
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Carrier> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (carrier?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	useEffect(() => {
		if (standsOfPart !== undefined) {
			setStandsSelected(standsOfPart || []);
		}
	}, [standsIsSuccess, standsOfPart]);

	useEffect(() => {
		if (modal !== "") {
			reset(carrier || CARRIER_INITIAL_VALUES);
			setIsSubmitLoading(false);
			if (carrier?.IsDuplicate) {
				if (standsOfPart && standsIsSuccess) {
					setStandsSelected(standsOfPart);
				}
			} else {
				setStandsSelected(standsOfPart || []);
			}
		}
	}, [modal, carrier]);

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						carrier === null ? "New Carrier" : `${carrier?.PartName ?? ""}`
					} - Carriers`}
				/>
			)}
			{carrier === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a carrier or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="CarrierStatusCode"
								control={control}
								render={({ field }) => (
									<TextField
										value={
											assemblyStatusCodes.find(
												(code) => code.Code === field.value,
											)?.Name
										}
										label="Assembly Status"
										variant="outlined"
										fullWidth
										size="small"
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
							<Controller
								name="RFIDTag"
								control={control}
								render={({ field }) => (
									<TextField
										label="Tag"
										variant="outlined"
										{...field}
										size="small"
										fullWidth
										disabled
									/>
								)}
							/>
						</Grid2>
						<StandGrid
							onSelectionChanged={setStandsSelected}
							disabled={modal === "delete"}
							standsOfPart={standsSelected}
						/>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
